import { CodigoBarras } from '@/componentes/etiqueta/CodigoBarras';

/**
 * O "Romaneio de Expedição" — o papel que o motorista assina.
 *
 * Porta do relatório `TELEVENDAS_ROMANEIO_EXPEDICAO` do Procfit, em A4
 * retrato. Mantido igual ao original de propósito, até o código do formulário
 * no rodapé (RSG-7502-02): é documento de expedição, alguém confere contra o
 * que já conhece, e "parecido" não serve.
 *
 * O CSS vive numa constante e não numa folha do aplicativo porque ele é usado
 * em dois lugares que não compartilham nada: a prévia em tela e o IFRAME de
 * impressão, que é autossuficiente (ver `imprimirRomaneio`). Todas as regras
 * são prefixadas por `.romaneio-papel` para não vazarem para o resto da
 * aplicação quando a prévia estiver aberta.
 */
export const CSS_ROMANEIO = `
.romaneio-papel {
  font-family: 'Liberation Sans', Arial, Helvetica, sans-serif;
  font-size: 7pt;
  color: #000;
  background: #fff;
  box-sizing: border-box;
}
.romaneio-papel *, .romaneio-papel *::before, .romaneio-papel *::after {
  box-sizing: border-box; margin: 0; padding: 0;
}
.romaneio-papel .rom-header { border: 1.5pt solid #000; margin-bottom: 4pt; padding: 4pt 6pt 2pt; }
.romaneio-papel .rom-header-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8pt; }
.romaneio-papel .rom-title-block { flex: 1; }
.romaneio-papel .rom-title { font-size: 13pt; font-weight: bold; text-align: center; border-bottom: 1pt solid #000; padding-bottom: 3pt; margin-bottom: 4pt; }
.romaneio-papel .rom-meta { display: flex; gap: 6pt; justify-content: space-between; font-size: 8pt; }
.romaneio-papel .rom-meta-cell { text-align: center; }
.romaneio-papel .rom-meta-label { font-size: 6pt; font-weight: bold; text-transform: uppercase; color: #555; }
.romaneio-papel .rom-meta-value { font-size: 10pt; font-weight: bold; }
.romaneio-papel .rom-meta-hora { font-size: 6.5pt; color: #666; margin-top: 0.5pt; }
.romaneio-papel .rom-carrier { text-align: right; min-width: 40%; }
.romaneio-papel .rom-carrier-label { font-size: 6pt; font-weight: bold; text-transform: uppercase; color: #555; }
.romaneio-papel .rom-carrier-name { font-size: 9pt; font-weight: bold; }
.romaneio-papel .rom-barcode { text-align: center; margin-top: 4pt; padding-top: 2pt; border-top: 0.5pt solid #ccc; }
.romaneio-papel .rom-barcode svg { height: 16mm; display: inline-block; }

.romaneio-papel table.detail { width: 100%; border-collapse: collapse; font-size: 6pt; margin-bottom: 6pt; }
.romaneio-papel table.detail th {
  background: #1e293b; color: #fff; padding: 2.5pt 3pt; font-size: 5.5pt; font-weight: bold;
  text-transform: uppercase; letter-spacing: 0.04em; border: 0.3pt solid #000; white-space: nowrap;
}
.romaneio-papel table.detail td {
  padding: 2.5pt 3pt; border-bottom: 0.3pt dashed #aaa;
  border-left: 0.3pt solid #ddd; border-right: 0.3pt solid #ddd; vertical-align: top;
}
.romaneio-papel table.detail tbody tr:first-child td { border-top: 0.3pt solid #000; }
.romaneio-papel table.detail td.num { text-align: right; white-space: nowrap; }
.romaneio-papel table.detail td.ctr { text-align: center; }
.romaneio-papel table.detail td.bold { font-weight: bold; }
.romaneio-papel table.detail td.wrap { word-break: break-word; max-width: 35mm; }

.romaneio-papel .summary-box { border: 1pt solid #000; padding: 4pt 8pt; margin-bottom: 8pt; page-break-inside: avoid; break-inside: avoid; }
.romaneio-papel .summary-title { font-size: 9pt; font-weight: bold; text-align: center; border-bottom: 1pt solid #000; margin-bottom: 4pt; padding-bottom: 2pt; letter-spacing: 0.06em; }
.romaneio-papel .summary-row { display: flex; gap: 20pt; justify-content: center; font-size: 8pt; flex-wrap: wrap; }
.romaneio-papel .sum-item { display: flex; gap: 4pt; align-items: baseline; }
.romaneio-papel .sum-label { font-weight: bold; }
.romaneio-papel .sum-val { font-size: 9pt; font-weight: bold; }

.romaneio-papel .sig-section { border: 1pt solid #000; padding: 6pt 8pt; page-break-inside: avoid; break-inside: avoid; }
.romaneio-papel .cert-text { font-size: 7pt; text-align: center; margin-bottom: 8pt; font-style: italic; }
.romaneio-papel .sig-row { display: flex; gap: 12pt; justify-content: space-between; margin-bottom: 8pt; }
.romaneio-papel .sig-block { flex: 1; }
.romaneio-papel .sig-name-label { font-size: 6.5pt; font-weight: bold; margin-bottom: 10pt; }
.romaneio-papel .sig-line { border-bottom: 1pt solid #000; height: 14pt; margin-bottom: 2pt; }
.romaneio-papel .sig-sub-label { font-size: 6pt; text-align: center; font-weight: bold; text-transform: uppercase; letter-spacing: 0.04em; color: #555; }
.romaneio-papel .sig-footer-row { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 4pt; }
.romaneio-papel .sig-date { font-size: 7pt; }
.romaneio-papel .sig-code { font-size: 7pt; color: #555; }
`;

export interface LinhaRomaneio {
  televenda: string;
  /**
   * A televenda de verdade. `televenda` acima é o que o PAPEL imprime, e no
   * ramo Americanas isso é o pedido do marketplace — não serve de chave.
   */
  televendaNumero: number;
  /** A empresa deste pedido. Não sai no papel — só na tela. */
  empresa: number | null;
  pedidoInternet: string | null;
  objeto: string | null;
  cep: string | null;
  cidade: string | null;
  uf: string | null;
  agendamento: string | null;
  frete: number | null;
  pesoGramas: number;
  volumes: number;
  notaFiscal: number | null;
  seguro: boolean;
  tipoServico: string | null;
  tipoEntrega: string | null;
}

/** A hora em que o romaneio foi gerado (`DATA_HORA`), no relógio do galpão. */
function hora(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
}

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
}

export function RomaneioPapel({
  romaneio,
  data,
  transportadora,
  linhas,
  qtdePedidos,
  totalVolumes,
  totalPesoGramas,
}: {
  romaneio: number;
  data: string | null;
  transportadora: string | null;
  linhas: LinhaRomaneio[];
  qtdePedidos: number;
  totalVolumes: number;
  totalPesoGramas: number;
}): JSX.Element {
  return (
    <div className="romaneio-papel">
      <div className="rom-header">
        <div className="rom-header-top">
          <div className="rom-title-block">
            <div className="rom-title">Romaneio de Expedição</div>
            <div className="rom-meta">
              <div className="rom-meta-cell">
                <div className="rom-meta-label">Romaneio</div>
                <div className="rom-meta-value">{romaneio}</div>
              </div>
              <div className="rom-meta-cell">
                <div className="rom-meta-label">Data da Expedição</div>
                <div className="rom-meta-value">{dataCurta(data)}</div>
                {hora(data) ? <div className="rom-meta-hora">gerado às {hora(data)}</div> : null}
              </div>
            </div>
          </div>
          <div className="rom-carrier">
            <div className="rom-carrier-label">Transportadora</div>
            <div className="rom-carrier-name">{transportadora ?? '-'}</div>
          </div>
        </div>
        {/* 70 mm de largura, como no original: 198,4 pt. */}
        <div className="rom-barcode">
          <CodigoBarras texto={String(romaneio)} larguraPt={198.4} alturaPt={45.4} />
        </div>
      </div>

      <table className="detail">
        <thead>
          <tr>
            <th style={{ width: '6%' }}>Televenda</th>
            <th style={{ width: '12%' }}>Objeto</th>
            <th style={{ width: '7%' }}>CEP</th>
            <th style={{ width: '4%' }}>UF</th>
            <th style={{ width: '10%' }}>Cidade</th>
            <th style={{ width: '8%' }}>Agendamento</th>
            <th style={{ width: '6%', textAlign: 'right' }}>Frete</th>
            <th style={{ width: '7%', textAlign: 'right' }}>Peso(gr)</th>
            <th style={{ width: '4%', textAlign: 'center' }}>Vol.</th>
            <th style={{ width: '7%', textAlign: 'center' }}>N.Fiscal</th>
            <th style={{ width: '8%' }}>Ped.Web</th>
            <th style={{ width: '4%', textAlign: 'center' }}>Seg.</th>
            <th style={{ width: '9%' }}>Tipo Serv.</th>
            <th style={{ width: '8%' }}>Tipo Entrega</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={`${l.televenda}-${l.notaFiscal ?? i}`}>
              <td>{l.televenda}</td>
              <td className="wrap">{l.objeto ?? ''}</td>
              <td>{l.cep ?? ''}</td>
              <td className="ctr">{l.uf ?? ''}</td>
              <td className="bold">{l.cidade ?? ''}</td>
              <td>{dataCurta(l.agendamento)}</td>
              <td className="num">{l.frete === null ? '' : l.frete.toFixed(2)}</td>
              <td className="num">{l.pesoGramas}</td>
              <td className="ctr">{l.volumes}</td>
              <td className="ctr">{l.notaFiscal ?? ''}</td>
              <td>{l.pedidoInternet ?? ''}</td>
              <td className="ctr">{l.seguro ? 'S' : 'N'}</td>
              <td className="bold">{l.tipoServico ?? ''}</td>
              <td className="bold">{l.tipoEntrega ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="summary-box">
        <div className="summary-title">RESUMO GERAL</div>
        <div className="summary-row">
          <div className="sum-item">
            <span className="sum-label">Qtde Pedidos:</span>
            <span className="sum-val">{qtdePedidos}</span>
          </div>
          <div className="sum-item">
            <span className="sum-label">Total Peso(gr):</span>
            <span className="sum-val">{totalPesoGramas}</span>
          </div>
          <div className="sum-item">
            <span className="sum-label">Volumes:</span>
            <span className="sum-val">{totalVolumes}</span>
          </div>
        </div>
      </div>

      <div className="sig-section">
        <div className="cert-text">
          Certifico que retirei na data especificada abaixo todos os pedidos desta relação, estando,
          a partir deste momento, responsável pelos mesmos.
        </div>
        <div className="sig-row">
          <div className="sig-block">
            <div className="sig-name-label">Nome do responsável pela Retirada</div>
            <div className="sig-line" />
            <div className="sig-sub-label">Assinatura</div>
          </div>
          <div className="sig-block">
            <div className="sig-name-label">Nome do responsável pela Liberação</div>
            <div className="sig-line" />
            <div className="sig-sub-label">Assinatura</div>
          </div>
        </div>
        <div className="sig-footer-row">
          <div className="sig-date">Data: _____ / _____ / _________</div>
          <div className="sig-code">(RSG-7502-02)</div>
        </div>
      </div>
    </div>
  );
}
